<script lang="ts">
  // Based on script from pmndrs/drei
  // info: https://r3f.docs.pmnd.rs/advanced/scaling-performance#performance-monitoring
  // repo: https://github.com/pmndrs/drei/blob/master/src/core/PerformanceMonitor.tsx

  import { onMount } from 'svelte';
  import PerformanceMonitorGui from './PerformanceMonitorGui.svelte';

  // #region props
  export let isDisplayGui = false;
  export let iterations = 10;
  export let ms = 250;
  export let threshold = 0.75;
  export let step = 0.1;
  export let factor = 0.5;
  export let flipflops = Number.POSITIVE_INFINITY;
  export let bounds = (refreshrate: number) => (refreshrate > 100 ? [60, 130] : [40, 70]);
  export let onIncline: ((api: any) => void) | null = null;
  export let onDecline: ((api: any) => void) | null = null;
  export let onChange: ((api: any) => void) | null = null;
  export let onFallback: ((api: any) => void) | null = null;
  // #endregion

  // #region setup
  let lastFactor = 0;
  const decimalPlacesRatio = Math.pow(10, 0);
  export const api = {
    fps: 0,
    index: 0,
    factor,
    flipped: 0,
    refreshrate: 0,
    fallback: false,
    frames: [] as number[],
    averages: [] as number[],
  };
  // #endregion

  // #region CDP
  let trackerTimeout: NodeJS.Timeout;
  const trackPagePerformanceLevel = async () => {
    clearTimeout(trackerTimeout);
    trackerTimeout = setTimeout(() => {
      window.Sq.Tracking.track(
        {
          action: 'Set Page Performance Level',
          properties: {
            didFallback: api.fallback,
            numPerformanceUpdates: api.flipped,
          },
        },
        { destinations: ['cdp'] }
      );
    }, 250);
  };
  // #endregion

  // #region monitor
  // manually trigger fallback
  export const triggerFallback = () => {
    if (api.fallback) {
      return;
    }
    api.fallback = true;
    if (onFallback) {
      trackPagePerformanceLevel();
      onFallback(api);
    }
  };

  const monitor = () => {
    if (api.averages.length < iterations) {
      api.frames.push(performance.now());
      const msPassed = (api.frames?.at(-1) ?? 0) - (api.frames?.[0] ?? 0);
      if (msPassed >= ms) {
        api.fps = Math.round((api.frames.length / msPassed) * 1000 * decimalPlacesRatio) / decimalPlacesRatio;
        api.refreshrate = Math.max(api.refreshrate, api.fps);

        // stop adjusting performance when fallback is triggered
        if (!api.fallback) {
          api.averages[api.index++ % iterations] = api.fps;
          if (api.averages.length === iterations) {
            const [lower, upper] = bounds(api.refreshrate);
            const upperBounds = api.averages.filter((value) => value >= upper);
            const lowerBounds = api.averages.filter((value) => value < lower);

            // Trigger incline when more than -threshold- avgs exceed the upper bound
            if (upperBounds.length > iterations * threshold) {
              api.factor = Math.min(1, api.factor + step);
              if (onIncline) {
                api.flipped++;
                trackPagePerformanceLevel();
                onIncline(api);
              }
            }

            // Trigger decline when more than -threshold- avgs are below the lower bound
            if (lowerBounds.length > iterations * threshold) {
              api.factor = Math.max(0, api.factor - step);
              if (onDecline) {
                api.flipped++;
                trackPagePerformanceLevel();
                onDecline(api);
              }
            }

            if (lastFactor !== api.factor) {
              lastFactor = api.factor;
              if (onChange) {
                trackPagePerformanceLevel();
                onChange(api);
              }
            }

            if (api.flipped > flipflops && !api.fallback) {
              api.fallback = true;

              if (onFallback) {
                trackPagePerformanceLevel();
                onFallback(api);
              }
            }
            api.averages = [];

            // Resetting the refreshrate creates more problems than it solves atm
            // api.refreshrate = 0
          }
        }
        api.frames = [];
      }
    }
  };
  // #endregion

  // #region update
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let updateFps = (value: number, maxValue: number, updatedBottomMessage?: string) => {};

  const getBottomMessage = () => `FLIP: ${api.flipped}  FB: ${api.fallback ? 'Y' : 'N'}`;

  let loop: number;
  const tick = () => {
    monitor();
    // update the gui
    if (isDisplayGui) {
      updateFps(api.fps, api.refreshrate, getBottomMessage());
    }
    // recurse
    loop = requestAnimationFrame(tick);
  };
  // #endregion

  // #region lifecycle
  onMount(() => {
    trackPagePerformanceLevel();
    tick();
    return () => {
      cancelAnimationFrame(loop);
    };
  });
  // #endregion
</script>

{#if isDisplayGui}
  <div class="gui">
    <PerformanceMonitorGui name="FPS" fg="#0ff" bg="#002" bottomMessage={getBottomMessage()} bind:update={updateFps} />
  </div>
{/if}

<style lang="scss">
  @layer standard-components {
    .gui {
      z-index: 9999;

      display: flex;
      gap: 0.4rem;

      position: fixed;
      top: 0;
      left: 0;
      margin: 0.4rem;
      width: 8rem;
      height: 6rem;
    }
  }
</style>
